import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { setUnauthenticatedHandler } from './api'
import { logout, resume } from './auth'
import Logo from './components/Logo'
import SiteFooter from './components/SiteFooter'
import ThemeToggle from './components/ThemeToggle'
import About from './pages/About'
import Account from './pages/Account'
import DeckDetail from './pages/DeckDetail'
import Decks from './pages/Decks'
import ForgotPassword from './pages/ForgotPassword'
import Images from './pages/Images'
import Learn from './pages/Learn'
import Login from './pages/Login'
import NewDeck from './pages/NewDeck'
import Privacy from './pages/Privacy'
import SignUp from './pages/SignUp'
import Terms from './pages/Terms'
import Test from './pages/Test'

type AuthState = 'checking' | 'signed-in' | 'signed-out'

export default function App() {
  const [auth, setAuth] = useState<AuthState>('checking')
  const location = useLocation()

  const signedOut = useCallback(() => setAuth('signed-out'), [])
  const markSignedIn = useCallback(() => setAuth('signed-in'), [])

  useEffect(() => {
    setUnauthenticatedHandler(signedOut)
    // A refresh cookie survives a page reload; an access token does not, because it is
    // only ever held in memory.
    resume()
      .then((ok) => setAuth(ok ? 'signed-in' : 'signed-out'))
      .catch(() => setAuth('signed-out'))
    return () => setUnauthenticatedHandler(null)
  }, [signedOut])

  if (auth === 'checking') {
    return (
      <>
        <SiteHeader signedIn={false} onSignedOut={signedOut} />
        <main className="layout">
          <p className="muted">Loading…</p>
        </main>
      </>
    )
  }

  return (
    <>
      <SiteHeader signedIn={auth === 'signed-in'} onSignedOut={signedOut} />
      <main className="layout">
        <Routes>
          {/* Reachable signed in too: confirming a recent password is how an operation
              like account deletion gets a fresh `auth_time` (spec §8.4). */}
          <Route path="/login" element={<Login onSignedIn={markSignedIn} />} />
          {/* Public: Stripe, sign-up and the footer all link here, signed in or not. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {auth === 'signed-in' ? (
            <>
              <Route path="/" element={<Navigate to="/decks" replace />} />
              <Route path="/decks" element={<Decks />} />
              <Route path="/decks/new" element={<NewDeck />} />
              <Route path="/decks/:deckId" element={<DeckDetail />} />
              <Route path="/decks/:deckId/learn" element={<Learn />} />
              <Route path="/decks/:deckId/test" element={<Test />} />
              <Route path="/test/all" element={<Test />} />
              <Route path="/images" element={<Images />} />
              <Route path="/account" element={<Account onSignedOut={signedOut} />} />
              <Route path="/pricing" element={<PricingRedirect />} />
              <Route path="*" element={<Navigate to="/decks" replace />} />
            </>
          ) : (
            // Any deep link while signed out lands here, with the intended path kept so
            // the round trip through the identity provider returns to it (spec §8.1).
            <>
              <Route path="/signup" element={<SignUp onSignedIn={markSignedIn} />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="*" element={<About returnTo={location.pathname} />} />
            </>
          )}
        </Routes>
      </main>
      <SiteFooter />
    </>
  )
}

/** The plan picker lives on /account. /pricing is still linked from quota problems
 *  (`upgradeUrl`) and from Checkout sessions created before issue #18, so forward it
 *  with its query string intact rather than letting the catch-all drop it. */
function PricingRedirect() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '/account', search }} replace />
}

/** Shown signed out too, so the theme toggle is always in the top right. */
function SiteHeader({
  signedIn,
  onSignedOut,
}: {
  signedIn: boolean
  onSignedOut: () => void
}) {
  return (
    <header className="site">
      <div className="inner">
        <Link className="brand" to={signedIn ? '/decks' : '/'}>
          <Logo />
          Flightdeck
        </Link>
        {signedIn ? (
          <nav>
            <NavLink to="/decks">Decks</NavLink>
            <NavLink to="/images">Images</NavLink>
            <NavLink to="/account">Account</NavLink>
            <button
              className="link"
              onClick={() => {
                logout().catch(() => onSignedOut())
              }}
            >
              Log out
            </button>
          </nav>
        ) : null}
        <ThemeToggle />
      </div>
    </header>
  )
}
